import React, { useState, useEffect, useCallback } from "react";

import { Collapse, Spin, Button, Modal, message, Row } from "antd";
import moment from "moment";
import { CaretRightOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import CodReconcilationFilter from "./../CodReconcilation/CodReconcilationFilter";
import PageHeader from "../Layout/PageHeader";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";
import { SERVICE_PROVIDER_DETAILS } from "../../constants/serviceProviders";
import { formatDate } from "../../helpers/date";

const todayDate = moment();
const startOfMonth = moment(todayDate).startOf("month");
const endOfMonth = moment(todayDate).endOf("month");

const CodCalendar = () => {
  const { permissions } = usePermissions();
  const [days, setDays] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());
  const [collapsed, setCollapsed] = useState({});
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    startDate: startOfMonth,
    endDate: endOfMonth,
    isRemittance: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const getDaysInMonth = (year, month) => {
    const firstDayOfMonth = moment(`${year}-${month}-01`);
    const daysInMonth = firstDayOfMonth.daysInMonth();
    const startDay = firstDayOfMonth.day();

    let calendarDays = [];
    for (let i = 0; i < startDay; i++) {
      calendarDays.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }

    setDays(calendarDays);
  };

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setData([]);

      const res = await axios.get("/cod-reconcilation", {
        params: {
          ...(filters.startDate !== null && {
            startDate: filters.startDate?.startOf("month")?.toISOString(),
          }),
          ...(filters.endDate !== null && {
            endDate: filters.endDate?.endOf("month")?.toISOString(),
          }),
          ...(filters.isRemittance !== null && {
            isRemittance: filters.isRemittance,
          }),
        },
        timeout: 180000,
      });

      const rawData = res.data.data || [];

      const transformedData = Object.entries(rawData).map(([date, info]) => ({
        date,
        ...info,
      }));

      setData(transformedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  const downloadCSV = async (date = null) => {
    setIsDownloading(true);

    const messageContent = (
      <p>
        Are you sure you want to download orders data for{" "}
        <b>
          {date
            ? date
            : `${formatDate(
              date ? date : filters.startDate?.startOf("month")?.toISOString()
            )} - ${formatDate(
              date ? date : filters.endDate?.endOf("month")?.toISOString()
            )}`}
        </b>
        ?
      </p>
    );

    Modal.confirm({
      content: messageContent,
      title: `Download COD Reconciliation List?`,
      async onOk() {
        try {
          await axios.get("cod-reconcilation/csv", {
            params: {
              ...(filters.startDate !== null && {
                startDate: date
                  ? moment(date).endOf("day").toISOString()
                  : filters.startDate?.startOf("month")?.toISOString(),
              }),
              ...(filters.endDate !== null && {
                endDate: date
                  ? moment(date).endOf("day").toISOString()
                  : filters.endDate?.endOf("month")?.toISOString(),
              }),
              ...(filters.isRemittance !== null && {
                isRemittance: filters.isRemittance,
              }),
            },
          });
          message.info(`Please check your email after 5–20 minutes.`);
        } catch (error) {
          message.error(error.message);
        }
      },
    });
    setIsDownloading(false);
  };

  useEffect(() => {
    setCurrentDate(moment(filters.startDate));
    getDaysInMonth(filters.startDate.year(), filters.startDate.month() + 1);
  }, [filters.startDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleCollapse = (day, index) => {
    setCollapsed((prevState) => ({
      ...prevState,
      [`${day}-${index}`]: !prevState[`${day}-${index}`],
    }));
  };

  return (
    <div>
      <PageHeader
        title="COD Reconcilation Calender"
        childRoutes={[
          {
            path: "/cod-calender",
            breadcrumbName: "COD Reconcilation Calender",
          },
        ]}
      />

      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <CodReconcilationFilter
            filters={filters}
            onFiltersChange={setFilters}
          />
        </Collapse.Panel>
      </Collapse>

      <Row className="mt-2 mb-2" justify="end" align="end">
        {permissions?.includes(PERMISSIONS.EXPORT_COD_RECONCILATION_REPORT) && (
          <Button onClick={() => downloadCSV()} loading={isDownloading}>
            CSV <DownloadOutlined />
          </Button>
        )}
      </Row>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : (
        permissions?.includes(PERMISSIONS.VIEW_COD_RECONCILATION_CALENDER) && (
          <div
            style={{
              width: "100%",
              maxWidth: "1500px",
              margin: "20px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                fontSize: "1.8em",
                fontWeight: "bold",
                background: "#4a90e2",
                color: "#fff",
                padding: "15px 0",
              }}
            >
              {currentDate.format("MMMM YYYY")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                textAlign: "center",
                fontWeight: "bold",
                background: "#f4f4f9",
                padding: "10px 0",
              }}
            >
              <span>Sun</span>
              <span>Mon</span>
              <span>Tue</span>
              <span>Wed</span>
              <span>Thu</span>
              <span>Fri</span>
              <span>Sat</span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "1px",
              }}
            >
              {days.map((day, index) => {
                if (!day)
                  return <div key={index} style={{ minHeight: "100px" }}></div>;

                const formattedDate = currentDate
                  .date(day)
                  .format("YYYY-MM-DD");
                const dayData = data.find(
                  (item) => item.date === formattedDate
                );

                return (
                  <div
                    key={index}
                    style={{
                      minHeight: "100px",
                      padding: "5px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <div
                      style={
                        day === todayDate.date()
                          ? {
                            fontWeight: "bold",
                            background: "#cce5ff",
                            borderRadius: "50%",
                            padding: "5px",
                            display: "inline-block",
                          }
                          : { fontWeight: "bold" }
                      }
                    >
                      {day}
                    </div>

                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "0.85em",
                        color: "#333",
                      }}
                    >
                      {dayData && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            height: "100%",
                            padding: "10px",
                            borderRadius: "8px",
                            background: "#d1ecf1",
                            color: "#0c5460",
                            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                            transition: "background 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              flexGrow: 1,
                            }}
                            onClick={() => toggleCollapse(day, index)}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#bde0eb")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "#d1ecf1")
                            }
                          >
                            <div
                              style={{
                                fontWeight: "bold",
                                fontSize: "1em",
                                marginBottom: "5px",
                              }}
                            >
                              COD Summary
                            </div>
                            <ul
                              style={{
                                padding: "0",
                                margin: "0",
                                listStyleType: "none",
                              }}
                            >
                              <li>
                                <strong>Total COD:</strong> RM{" "}
                                {dayData.totalCod.toFixed(2)}
                              </li>
                              <li>
                                <strong>Total Orders:</strong>{" "}
                                {dayData.totalOrderCount}
                              </li>
                              <li>
                                <strong>Total Received:</strong> RM{" "}
                                {dayData.totalRemittedCod.toFixed(2)}
                              </li>
                              <li>
                                <strong>Total Unsettled:</strong> RM{" "}
                                {dayData.totalUnremittedCod.toFixed(2)}
                              </li>
                            </ul>

                            {collapsed[`${day}-${index}`] && (
                              <div
                                style={{
                                  marginTop: "6px",
                                  padding: "8px",
                                  background: "#f8f9fa",
                                  borderRadius: "6px",
                                  fontSize: "0.9em",
                                  color: "#333",
                                  boxShadow:
                                    "inset 0px 0px 5px rgba(0, 0, 0, 0.1)",
                                }}
                              >
                                {dayData.providers.map((sp, j) => {
                                  const providerName =
                                    SERVICE_PROVIDER_DETAILS.find(
                                      (p) => p.key === sp.provider
                                    )?.name || sp.provider;
                                  return (
                                    <div key={j} style={{ padding: "2px 0" }}>
                                      <strong>{providerName}:</strong> RM{" "}
                                      {sp.totalCod.toFixed(2)}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {permissions?.includes(PERMISSIONS.EXPORT_COD_RECONCILATION_REPORT) && (
                            <Button
                              onClick={() => downloadCSV(formattedDate)}
                              loading={isDownloading}
                              style={{
                                marginTop: "20px",
                                alignSelf: "center",
                                width: "100%",
                              }}
                            >
                              CSV <DownloadOutlined />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default CodCalendar;
